import React, { useEffect, useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import useUserData from 'hooks/User';
import { useNavigate } from 'react-router-dom';

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isLogged, setIsLogged] = useState(true);


  const navigate = useNavigate();
  const user = useUserData();


  const logout = () => {
    setIsLogged(false);
  };
  useEffect(() => {
    if (!isLogged) {
      localStorage.clear();
      navigate('/login');
      window.location.reload();
    }
  }, [navigate, isLogged]);


  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim().length > 0) {
      // Simulate search results - in real app, this would be an API call
      const mockResults = [
        { type: 'card', id: 'NFC-001', name: 'John Doe Business Card', status: 'active' },
        { type: 'card', id: 'NFC-002', name: 'Jane Smith Contact Card', status: 'inactive' },
        { type: 'user', id: 'USR-001', name: 'John Doe', email: 'john@example.com' },
        { type: 'user', id: 'USR-002', name: 'Jane Smith', email: 'jane@example.com' },
        { type: 'billing', id: 'INV-001', name: 'Invoice #001', amount: '$299.99' },
      ].filter(item =>
        item.name.toLowerCase().includes(query.toLowerCase()) ||
        item.id.toLowerCase().includes(query.toLowerCase())
      );

      setSearchResults(mockResults);
      setShowResults(true);
    } else {
      setShowResults(false);
    }
  };

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    if (searchQuery.trim().length > 0) {
      setShowResults(true);
    }
  };

  const handleSearchBlur = () => {
    setIsSearchFocused(false);
    // Delay hiding results to allow clicking on them
    setTimeout(() => setShowResults(false), 200);
  };

  const handleResultClick = (result) => {
    setSearchQuery('');
    setShowResults(false);
    // Navigate to appropriate page based on result type
    const routes = {
      card: '/card-management',
      user: '/user-management',
      billing: '/billing-management'
    };
    window.location.href = routes[result.type] || '/dashboard';
  };

  const getResultIcon = (type) => {
    const icons = {
      card: 'CreditCard',
      user: 'User',
      billing: 'Receipt'
    };
    return icons[type] || 'Search';
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'text-success',
      inactive: 'text-text-muted',
      pending: 'text-warning'
    };
    return colors[status] || 'text-text-secondary';
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-surface border-b border-border">
      <div className="flex items-center justify-between px-6 py-4">
        {/* Mobile Menu Button - Hidden on desktop */}
        <Button
          variant="ghost"
          className="lg:hidden p-2"
          onClick={() => {
            // Toggle mobile sidebar
            const sidebar = document.getElementById('mobile-sidebar');
            if (sidebar) {
              sidebar.classList.toggle('-translate-x-full');
            }
          }}
        >
          <Icon name="Menu" size={20} />
        </Button>

        {/* Page Title - Hidden on mobile when search is focused */}
        <div className={`flex-1 lg:flex-none ${isSearchFocused ? 'hidden' : 'block'} lg:block`}>
          <h1 className="text-lg font-semibold text-text-primary">
            NFC Card Admin
          </h1>
        </div>

        {/* Global Search */}
        <div className="relative flex-1 max-w-md mx-4 lg:mx-8">
          <div className="relative">
            <Icon
              name="Search"
              size={18}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted"
            />
            <input
              type="text"
              placeholder="Search cards, users, or billing..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-150"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setShowResults(false);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors duration-150"
              >
                <Icon name="X" size={16} />
              </button>
            )}
          </div>

          {/* Search Results Dropdown */}
          {showResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-surface border border-border rounded-lg shadow-elevation-3 z-70 max-h-80 overflow-y-auto">
              <div className="p-2">
                <div className="text-xs font-medium text-text-muted uppercase tracking-wide px-3 py-2">
                  Search Results ({searchResults.length})
                </div>
                {searchResults.map((result, index) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleResultClick(result)}
                    className="w-full flex items-center px-3 py-2 text-left hover:bg-hover rounded-lg transition-colors duration-150"
                  >
                    <Icon
                      name={getResultIcon(result.type)}
                      size={16}
                      className="text-text-muted mr-3 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-primary truncate">
                        {result.name}
                      </div>
                      <div className="text-xs text-text-muted">
                        {result.type.charAt(0).toUpperCase() + result.type.slice(1)} • {result.id}
                        {result.status && (
                          <span className={`ml-2 ${getStatusColor(result.status)}`}>
                            {result.status}
                          </span>
                        )}
                        {result.email && (
                          <span className="ml-2">{result.email}</span>
                        )}
                        {result.amount && (
                          <span className="ml-2 font-mono">{result.amount}</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* No Results */}
          {showResults && searchResults.length === 0 && searchQuery.trim().length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-surface border border-border rounded-lg shadow-elevation-3 z-70">
              <div className="p-4 text-center">
                <Icon name="Search" size={24} className="text-text-muted mx-auto mb-2" />
                <div className="text-sm text-text-muted">
                  No results found for "{searchQuery}"
                </div>
              </div>
            </div>
          )}
        </div>

        {/* User Actions */}
        <div className="flex items-center space-x-2">


          {/* User Profile */}
          <Button variant="ghost" className="p-2" onClick={() => logout()}>
            <Icon name="LogIn" size={20} />
          </Button>
          <p>{user?.name || user?.email}</p>
        </div>
      </div>
    </header>
  );
};

export default Header;